import { Component, OnInit } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { AnalyticsService } from './services/gtag.service';
import { TopicService } from './services/topic.service';
import { Topic } from './entities/topic.model';
import { User } from './entities/user.model';
import { AppTranslateService } from './services/translate.service';
import { UserService } from './services/user.service';
import { Platform } from '@ionic/angular';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { AuthService } from './services/auth.service';
import { DisplayService } from './services/display.service';
import { SplashScreen } from '@capacitor/splash-screen';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  topics: Topic[] = [];
  user: User | null = null;
  nativeLang: string = 'en'; // Default language
  targetLang: string = 'en'; // Default language
  completedTour: boolean = false; // Track if the user has completed the tour
  private translationInitialized = false;

  constructor(
    private topicService: TopicService,
    public authService: AuthService,
    private translateService: AppTranslateService,
    public userService: UserService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private analytics: AnalyticsService,
    private platform: Platform,
    private displayService: DisplayService
  ) {}

  async ngOnInit() {
    // Apply display preferences immediately at app level (before any page loads)
    const currentUser = this.authService.currentUserValue;
    this.displayService.updateFromUser(currentUser);
    
    // Show splash screen for 2.5 seconds and auto-hide
    await SplashScreen.show({
      showDuration: 2500,
      autoHide: true,
    });
    
    // Initialize translation service first to prevent placeholder text
    this.initializeLang();
    
    // Subscribe to authentication state changes for UI state management only
    this.authService.currentUser$.subscribe(user => {
      this.user = user;
      
      if (this.user) {
        // User is logged in - update UI state
        this.nativeLang = this.user.nativeLanguage;
        this.targetLang = this.user.targetLanguage;
        this.completedTour = this.user.completedTour;
        
        // Set user ID for analytics tracking
        this.analytics.setUserId(this.user.uid);
        
        this.loadTopics();
      }
      
      // Update display preferences at app level whenever user changes
      this.displayService.updateFromUser(this.user);
      
      // Note: Navigation is now handled by guards, not here
    });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        // Track analytics for navigation
        const screenName = this.determineScreenName(event);
        this.analytics.trackView(event.urlAfterRedirects, screenName);
      }
    });

    // Kick off a native Firebase Auth test without blocking init
    void this.testNativeAuth();
  }



  initializeLang() {
    let route = this.activatedRoute.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    this.translateService.initializeTranslation(this.activatedRoute);
  }

  determineScreenName(event: NavigationEnd): string {
    let route = this.activatedRoute.root;
    let screenName = '';
    while (route.firstChild) {
      route = route.firstChild;
    }
    const routeConfig = route.routeConfig;
    if (routeConfig && routeConfig.path) {
      // Customize screen name based on route
      if (routeConfig.path === 'news/:topic') {
        const topic = route.snapshot.paramMap.get('topic');
        screenName = topic ? `News - ${topic}` : 'News';
      } else if (routeConfig.path === 'article/:id') {
        const id = route.snapshot.paramMap.get('id');
        screenName = id ? `Article - ${id}` : 'Article';
      } else if (routeConfig.path === 'login') {
        screenName = 'Login';
      } else if (routeConfig.path === 'signup') {
        screenName = 'Sign Up';
      } else if (routeConfig.path === 'settings') {
        screenName = 'Settings';
      } else if (routeConfig.path === 'welcome') {
        screenName = 'Welcome';
      } else if (routeConfig.path === 'study') {
        screenName = 'Study';
      } else {
        screenName = routeConfig.path;
      }
    } else {
      screenName = event.urlAfterRedirects;
    }
    return screenName;
  }

  loadTopics() {
    this.topicService.getTopics(this.targetLang).subscribe((topics) => {
      this.topics = topics;
    });
  }

  filterTopic(topic: Topic) {
    this.topicService.selectTopic(topic);
  }

  // Test native Firebase Authentication plugin on iOS/Android
  private async testNativeAuth(): Promise<void> {
    await this.platform.ready();
    if (this.platform.is('ios') || this.platform.is('android')) {
      const user = await FirebaseAuthentication.getCurrentUser();
      console.log('Native Firebase current user:', user);
    } else {
      console.log('Running in web browser, skipping native plugin test.');
    }
  }
}
